import Header from './header';
import Navbar from './Navbar';
import Footer from './Footer';
import './App.css';
import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Link } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import WhoIAm from './components/WhoIAm';
import Weddings from './components/Weddings';
import Christening from './components/Christening';
import Newborn from './components/Newborn';
import RealEstate from './components/RealEstate';
import B2B from './components/B2B';
import Pricing from './components/Pricing';
import Contact from './components/Contact';
import Gallery from './components/Gallery';
import Blog from './components/Blog';
import BlogPost from './components/BlogPost';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import ReturnPolicy from './components/ReturnPolicy';
import DigitalPolicy from './components/DigitalPolicy';
import CookieConsentBanner from './components/CookieConsent';

// ScrollToTop component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

const testimonials = [
  {
    image: '/images/GAwedding/GAwedding-2.jpg',
    text: `We are beyond grateful for the incredible memories captured on our wedding day. From the moment we met, Tamas made us feel at ease. The photos and video tell the story of our day so beautifully—we still tear up watching it! Every detail, every glance, every laugh was caught so naturally. Thank you for giving us something we'll treasure forever."`,
    author: 'Szilard & Anetta',
  },
  {
    image: '/images/DDwedding/DDwedding-8.jpg',
    text: `Tamas was honestly amazing on our wedding day. Super calm and easy to be around, and he somehow caught all the best moments without us even noticing. The photos turned out beautiful—so natural and full of emotion. We've had loads of compliments. Couldn't be happier!`,
    author: 'Dragos & Diana',
  },
  {
    image: '/images/hotelshooting-7.jpg',
    text: `We recently worked with Tamas for a photo shoot of our new extension at Hampton by Hilton, and the results were outstanding. He captured the space beautifully—clean, modern, and exactly how we wanted it presented. His attention to detail and professionalism throughout the shoot made the whole process easy and enjoyable. Highly recommend for any real estate or commercial photography!`,
    author: 'Hampton by Hilton team',
  },
];



const latestWorks = [
  {
    title: 'Hampton By Hilton Shooting',
    image: '/images/hotelshooting-8.jpg',
    button: 'VIEW ALBUM',
    album: [
      '/images/hotelshooting-7.jpg',
      '/images/hotelshooting-11.jpg',
      '/images/hotelshooting-12.jpg',
      '/images/hotelshooting-15.jpg'
      
    ]
  },
  {
    title: 'Gabor & Anetta Wedding',
    image: '/images/GAwedding/GAwedding-8.jpg',
    button: 'VIEW ALBUM',
    album: [
      '/images/GAwedding/GAwedding-3.jpg',
      '/images/GAwedding/GAwedding-4.jpg',
      '/images/GAwedding/GAwedding-6.jpg',
      '/images/GAwedding/GAwedding-13.jpg'
    ]
  },
  {
    title: 'Hampton By Hilton Details',
    image: '/images/hotelshooting-12.jpg',
    button: 'VIEW ALBUM',
    album: [
      '/images/hotelshooting.jpg',
      '/images/hotelshooting-2.jpg',
      '/images/hotelshooting-3.jpg',
      '/images/hotelshooting-4.jpg',
      '/images/hotelshooting-5.jpg',
      '/images/hotelshooting-6.jpg',
      '/images/hotelshooting-7.jpg'
    ]
  },
];

function App() {
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [isAlbumOpen, setIsAlbumOpen] = useState(false);
  const [albumIndex, setAlbumIndex] = useState(0);
  const [currentAlbum, setCurrentAlbum] = useState([]);
  const [showGoTop, setShowGoTop] = useState(false);
  const [workIndex, setWorkIndex] = useState(0);
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 700;

  useEffect(() => {
    const timer = setInterval(() => {
      setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    const handleScroll = () => {
      setShowGoTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      clearInterval(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const openAlbum = (idx = 0) => {
    setCurrentAlbum(latestWorks[idx].album);
    setAlbumIndex(0);
    setIsAlbumOpen(true);
  };
  const closeAlbum = () => setIsAlbumOpen(false);
  const nextAlbum = () => setAlbumIndex((prev) => (prev + 1) % currentAlbum.length);
  const prevAlbum = () => setAlbumIndex((prev) => (prev - 1 + currentAlbum.length) % currentAlbum.length);

  const nextWork = () => setWorkIndex((prev) => (prev + 1) % latestWorks.length);
  const prevWork = () => setWorkIndex((prev) => (prev - 1 + latestWorks.length) % latestWorks.length);

  const handleGoTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!isAlbumOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeAlbum();
      if (e.key === 'ArrowRight') nextAlbum();
      if (e.key === 'ArrowLeft') prevAlbum();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAlbumOpen, currentAlbum, albumIndex]);

  return (
    <HelmetProvider>
      <div className="app-container">
        <Helmet>
          <title>Szakacs Visuals - Professional Photography & Videography Services</title>
          <meta name="description" content="Szakacs Visuals specializes in wedding photography and videography, capturing timeless moments of your special day. Based in Bristol, UK, we offer professional services including product photography, real estate visuals, and corporate branding." />
          <meta name="keywords" content="wedding photography, wedding videography, Bristol photographer, professional photography, real estate photography, corporate photography" />
          <meta property="og:title" content="Szakacs Visuals - Professional Photography & Videography Services" />
          <meta property="og:description" content="Professional photography and videography services in Bristol, UK. Specializing in weddings, engagements, real estate, and corporate photography." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://szakacsvisuals.co.uk" />
          <meta property="og:image" content="https://szakacsvisuals.co.uk/og-cover.jpg" />
          <meta property="og:image:alt" content="Szakacs Visuals Photography" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <link rel="canonical" href="https://szakacsvisuals.co.uk" />
        </Helmet>
        <ScrollToTop />
        <Navbar />
        <Routes>
          <Route path="/whoiam" element={<WhoIAm />} />
          <Route path="/services/weddings" element={<Weddings />} />
          <Route path="/services/christening" element={<Christening />} />
          <Route path="/services/newborn" element={<Newborn />} />
          <Route path="/services/real-estate" element={<RealEstate />} />
          <Route path="/services/b2b" element={<B2B />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/returns" element={<ReturnPolicy />} />
          <Route path="/digital-policy" element={<DigitalPolicy />} />
          <Route path="*" element={
            <>
              <Header />
              <div className="latest-work-overlay">
                <div className="latest-work-content">
                  <div className="latest-work-text">
                    <h2>OUR LATEST WORK</h2>
                  </div>
                </div>
                <div className="latest-work-slider-container">
                  <button className="work-arrow left" onClick={prevWork} aria-label="Previous work" style={{display: latestWorks.length > 1 ? 'block' : 'none'}}>&#8592;</button>
                  <div className="latest-work-slider">
                    {latestWorks.map((work, idx) => {
                      if (isMobile && idx !== workIndex) return null;
                      return (
                        <div className="latest-work-card" key={idx}>
                          <img src={work.image} alt={work.title} className="latest-work-card-img" />
                          <div className="latest-work-card-title">{work.title}</div>
                          <button className="view-album-btn" onClick={() => openAlbum(idx)}>{work.button}</button>
                        </div>
                      );
                    })}
                  </div>
                  <button className="work-arrow right" onClick={nextWork} aria-label="Next work" style={{display: latestWorks.length > 1 ? 'block' : 'none'}}>&#8594;</button>
                </div>
              </div>
              <main className="main-content">
                {/* Client Testimonials Section */}
                <section className="client-testimonials-section">
                  <h2 className="client-testimonials-title">CLIENT TESTIMONIALS</h2>
                  <div className="testimonial-carousel">
                    <div className="testimonial-slide">
                      <div className="testimonial-image-container">
                        <img src={testimonials[testimonialIndex].image} alt={testimonials[testimonialIndex].author} />
                      </div>
                      <div className="testimonial-text-container">
                        <div className="testimonial-text">{testimonials[testimonialIndex].text}</div>
                        <div className="testimonial-author">{testimonials[testimonialIndex].author}</div>
                      </div>
                    </div>
                    <div className="testimonial-dots">
                      {testimonials.map((_, idx) => (
                        <button
                          key={idx}
                          className={idx === testimonialIndex ? 'dot active' : 'dot'}
                          onClick={() => setTestimonialIndex(idx)}
                          aria-label={`Go to testimonial ${idx + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                </section>
                {/* Expert Visuals Section */}
                <section className="expert-visuals-section">
                  <div className="expert-visuals-container">
                    <div className="expert-visuals-image" />
                    <div className="expert-visuals-content">
                      <h2>EXPERT VISUALS<br />FOR YOUR<br />SPECIAL OCCASION</h2>
                      <p>
                        Szakacs Visuals specializes in wedding photography and videography, capturing timeless moments of your special day. Based in Bristol, UK, we also offer professional services including product photography, real estate visuals, and corporate branding.
                      </p>
                      <Link to="/contact">
                        <button className="get-in-touch-btn">GET IN TOUCH</button>
                      </Link>
                    </div>
                  </div>
                </section>
                {/* Partners Section - Only on main page */}
                {/* <section className="partners-section">
                  <h2>Our Partners</h2>
                  <div className="partners-grid">
                    <div className="partner-logo">
                      <a href="https://hampton-creative.co.uk/" target="_blank" rel="noopener noreferrer">
                        <img src="./images/hampton.png" alt="Hampton Creative Logo" />
                      </a>
                    </div>
                  </div>
                </section> */}
              </main>
            </>
          } />
        </Routes>
        {/* Album Modal */}
        {isAlbumOpen && (
          <div className="album-modal-overlay" onClick={closeAlbum}>
            <div className="album-modal" onClick={e => e.stopPropagation()}>
              <button className="album-close-btn" onClick={closeAlbum}>&times;</button>
              <button className="album-arrow album-arrow-left" onClick={prevAlbum}>&#8592;</button>
              <img className="album-image" src={currentAlbum[albumIndex]} alt={`Album ${albumIndex + 1}`} />
              <button className="album-arrow album-arrow-right" onClick={nextAlbum}>&#8594;</button>
              <div className="album-indicators">
                {currentAlbum.map((_, idx) => (
                  <button
                    key={idx}
                    className={idx === albumIndex ? 'album-dot active' : 'album-dot'}
                    onClick={() => setAlbumIndex(idx)}
                    aria-label={`Go to album image ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        {showGoTop && (
          <button className="go-top-btn" onClick={handleGoTop} aria-label="Go to top">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>
          </button>
        )}
        <CookieConsentBanner />
        <Footer />
      </div>
    </HelmetProvider>
  );
}

export default App;